import { clsx } from 'clsx';
import React from 'react';
import { Opportunity as ApiOpportunity } from '../../../../../js/api/models/Opportunity';

import { StatusPill } from '../../../components/StatusPill';
import { TooltippedText } from '../../../components/tooltipped-text';
import { StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import { formatApiDate } from '../../../utils/date';

const CardHeading: React.FC<React.ComponentPropsWithoutRef<'p'>> = ({
  className,
  children,
  ...props
}) => {
  return (
    <p
      {...props}
      className={clsx(
        className,
        'tw:!text-[16px] tw:font-semibold tw:text-zinc-900 tw:dark:text-zinc-100 tw:!mb-0'
      )}
    >
      {children}
    </p>
  );
};

export const OpportunityCard: React.FC<{
  opportunity: ApiOpportunity;
  degreeMap: Record<string, string>;
  specialtyMap: Record<string, string>;
  shiftRateTypeMap: Record<string, string>;
  shiftRateTypeIconMap: Record<string, string>;
  isSelected?: boolean;
}> = ({
  opportunity,
  degreeMap,
  specialtyMap,
  shiftRateTypeMap,
  shiftRateTypeIconMap,
  isSelected = false,
}) => {
  return (
    <div
      className={clsx(
        'tw:h-full tw:bg-white tw:rounded-lg tw:shadow-sm tw:border tw:border-zinc-200 tw:border-l-brand-green-2 tw:border-l-6 tw:hover:shadow-md tw:hover:bg-muted tw:transition-shadow tw:cursor-pointer tw:dark:bg-zinc-700 tw:dark:border-zinc-400 tw:dark:hover:bg-zinc-600',
        isSelected && 'tw:!bg-brand-green-0/10 tw:!shadow-md'
      )}
    >
      <div className="tw:p-3">
        <div className="tw:flex tw:justify-between tw:items-start tw:mb-3">
          <CardHeading className="tw:line-clamp-2">
            <i className="far fa-fw fa-lg fa-suitcase-medical tw:mr-1" />
            {opportunity.code}
          </CardHeading>
          <div className="tw:flex tw:items-center tw:gap-2">
            <StatusPill
              scope={WorkflowScopes.Opportunity}
              status={opportunity.current_status || 'Unknown'}
            />
          </div>
        </div>
        <div className="tw:flex tw:flex-wrap tw:gap-2 tw:mb-3">
          <span className="tw:bg-zinc-100 tw:border tw:border-brand-green-0/20 tw:text-brand-green-0 tw:text-xs tw:px-2 tw:py-1 tw:rounded-full tw:dark:bg-brand-green-1/20 tw:dark:border-brand-green-1/30 tw:dark:text-brand-green-1">
            {specialtyMap[opportunity.specialty] || opportunity.specialty}
          </span>
          {opportunity.degrees?.map((deg) => (
            <span
              key={deg}
              className="tw:bg-zinc-100 tw:border tw:border-zinc-300 tw:text-zinc-700 tw:text-xs tw:px-2 tw:py-1 tw:rounded-full tw:dark:bg-zinc-600 tw:dark:border-zinc-500 tw:dark:text-zinc-300"
            >
              {degreeMap[deg] || deg}
            </span>
          ))}
        </div>
        <div className="tw:text-sm tw:text-zinc-600 tw:mb-3 tw:dark:text-zinc-300">
          <div className="tw:flex tw:items-center tw:gap-2 tw:mb-1">
            <i className="fas fa-fw fa-hospital tw:text-zinc-400 tw:dark:text-zinc-500" />
            <span>
              {opportunity.facility?.name || 'Unknown Facility'}
              {opportunity.alternate_facilities && opportunity.alternate_facilities.length > 0 && (
                <TooltippedText
                  tooltipContent={
                    opportunity.alternate_facilities?.map((facility) => facility.name).join(', ') ||
                    ''
                  }
                >
                  <span className="tw:text-zinc-500 tw:ml-1">
                    + {opportunity.alternate_facilities.length}{' '}
                    {opportunity.alternate_facilities.length === 1 ? 'other' : 'others'}
                  </span>
                </TooltippedText>
              )}
            </span>
          </div>
          <div className="tw:flex tw:items-center tw:gap-2 tw:mb-1">
            <i className="fas fa-fw fa-map-marker-alt tw:text-zinc-400 tw:dark:text-zinc-500" />
            <span>
              {opportunity.facility?.city && opportunity.facility?.state
                ? `${opportunity.facility.city}, ${opportunity.facility.state}`
                : '--'}
            </span>
          </div>
          <div className="tw:flex tw:items-center tw:gap-2 tw:mb-1">
            <i className="far fa-fw fa-calendar tw:text-zinc-400 tw:dark:text-zinc-500" />
            <span>
              {formatApiDate(opportunity.starts_on, {
                prefix: 'Starts on',
                emptyLabel: 'No Start Date',
              })}
            </span>
          </div>
          {(opportunity.pending_connections > 0 || opportunity.accepted_connections > 0) && (
            <div className="tw:flex tw:items-center tw:gap-2 tw:mb-1">
              <i className="fas fa-fw fa-paper-plane-top tw:text-zinc-400 tw:dark:text-zinc-500" />
              <span>
                {opportunity.pending_connections > 0 && (
                  <>
                    <a
                      href={`/app/organization/connections?queue=awaiting_provider&status=${StatusCodes.REQUESTED}&search=${opportunity.code}`}
                      className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:!no-underline tw:transition-colors tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
                    >
                      {opportunity.pending_connections} Pending
                    </a>{' '}
                    &bull;{' '}
                  </>
                )}
                <a
                  href={`/app/organization/connections?queue=all&status=${StatusCodes.AWAIT_CLEAR}%2c${StatusCodes.PEND_FAC_REV}%2c${StatusCodes.PEND_CONFIRM}%2c${StatusCodes.ON_HOLD}%2c${StatusCodes.PROV_ACTION_REQ}%2c${StatusCodes.AWAIT_PRVLG}%2c${StatusCodes.ACTIVE}%2c${StatusCodes.PAUSED}%2c${StatusCodes.WITHDRAWN}%2c${StatusCodes.TERMINATED}&search=${opportunity.code}`}
                  className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:!no-underline tw:transition-colors tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
                >
                  {opportunity.accepted_connections} Accepted
                </a>
              </span>
            </div>
          )}
          {(opportunity.rejected_connections > 0 || opportunity.canceled_connections > 0) && (
            <div className="tw:flex tw:items-center tw:gap-2 tw:mb-1">
              <i className="fas fa-fw fa-ban tw:text-zinc-400 tw:dark:text-zinc-500" />
              <span>
                {opportunity.canceled_connections > 0 && (
                  <>
                    <a
                      href={`/app/organization/connections?queue=closed&status=${StatusCodes.CANCELED}&search=${opportunity.code}`}
                      className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:!no-underline tw:transition-colors tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
                    >
                      {opportunity.canceled_connections} Canceled
                    </a>
                    {opportunity.rejected_connections > 0 && ' • '}
                  </>
                )}
                {opportunity.rejected_connections > 0 && (
                  <a
                    href={`/app/organization/connections?queue=closed&status=${StatusCodes.DECLINED}&search=${opportunity.code}`}
                    className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:!no-underline tw:transition-colors tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
                  >
                    {opportunity.rejected_connections} Rejected
                  </a>
                )}
              </span>
            </div>
          )}
          <div className="tw:flex tw:flex-row tw:flex-wrap tw:align-center tw:items-center tw:gap-3 tw:mt-2">
            {opportunity.shift_rate_types?.map((type) => {
              // Map shift type to settings key
              const shiftTypeToSettingsKey: Record<string, string> = {
                day: 'max_rate_day',
                night: 'max_rate_night',
                call: 'max_rate_call',
                '24h': 'max_rate_24h',
              };
              const rateKey = shiftTypeToSettingsKey[type];
              const rate = opportunity.settings?.[rateKey];
              const label = shiftRateTypeMap[type] || type;
              const tooltip = rate ? `Max Rate: $${rate}` : 'Max rate not set';
              return (
                <React.Fragment key={type}>
                  <TooltippedText tooltipContent={tooltip} placement="bottom">
                    <div
                      key={type}
                      className="tw:bg-white tw:border tw:border-zinc-300 tw:text-xs tw:px-2 tw:py-1 tw:rounded-full tw:flex tw:items-center tw:gap-1 tw:dark:bg-zinc-600 tw:dark:border-zinc-500"
                    >
                      <i
                        className={`fas fa-fw ${shiftRateTypeIconMap[type] || 'fa-question'} tw:text-zinc-500 tw:dark:text-zinc-400`}
                      />
                      <span className="tw:text-zinc-500 tw:dark:text-zinc-400">{label}</span>
                    </div>
                  </TooltippedText>
                </React.Fragment>
              );
            })}
          </div>
        </div>
        {/*{opportunity.description && (*/}
        {/*  <div className="tw:text-sm tw:text-zinc-600 tw:font-light tw:line-clamp-2 tw:border-t tw:border-zinc-200 tw:pt-2 tw:dark:text-zinc-400 tw:dark:border-zinc-500">*/}
        {/*    {opportunity.description || 'No description provided.'}*/}
        {/*  </div>*/}
        {/*)}*/}
      </div>
      {/* {opportunity.current_status?.title === 'Closed' && (
        <div className="tw:flex tw:justify-center tw:pb-3">
          <Button
            color="brand-green-outline"
            className="tw:mt-2 tw:px-6 tw:py-2"
            onClick={handleDuplicateClick}
          >
            <i className="far fa-clone" /> Duplicate
          </Button>
        </div>
      )} */}
      {(!opportunity.settings?.auto_match ||
        (opportunity.settings?.max_accepted_connections || 0) > 0) && (
        <div className="tw:flex tw:justify-end tw:px-3 tw:pb-2 tw:gap-2">
          {(opportunity.settings?.max_accepted_connections || 0) > 0 && (
            <TooltippedText
              tooltipContent={`Max Accepted Providers: ${opportunity.settings?.max_accepted_connections}`}
            >
              <div className="">
                <i className="far fa-fw fa-info-circle tw:text-brand-green-0" />
              </div>
            </TooltippedText>
          )}
          {!opportunity.settings?.auto_match && (
            <TooltippedText tooltipContent="Auto-match is disabled for this opportunity.">
              <div className="">
                <i className="fas fa-fw fa-warning tw:text-stoplight-warning" />
              </div>
            </TooltippedText>
          )}
        </div>
      )}
    </div>
  );
};

export default OpportunityCard;
